import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild, ElementRef } from '@angular/core';
import { PlayerSources, StringListResult, TeamOverridesDto, WatchlistModel, PlayerNoteDto, CreatePlayerNoteCommand, UpdatePlayerNoteCommand } from '../../../api/models';
import { CustomPositions } from '../../../enums/custom-positions';
import { PlayerService, RateService, WatchlistService, PlayerNotesService } from '../../../api/services';
import { PlayerPopoverService } from '../../../services/player-popover.service';
import { AuthService } from '../../../services/auth.service';
import { catchError, map, Observable, of, Subscription, take } from 'rxjs';
import { chainLoad } from '../../../helpers/chainLoad';
import { MessageService, ConfirmationService } from 'primeng/api';

interface DropdownOption {
  name: string;
  id: string;
}

interface Source {
  search: string;
  lastSearchResults: { query: string; results: DropdownOption[] } | null;
  selectedOption: string | null;
  timer?: number;
  filteredOptions: DropdownOption[];
  options?: DropdownOption[];
  dataSource?: keyof RateService;
  sourceName: PlayerSources;
}

interface Sources {
  evanmiya: Source;
  realGM: Source;
  stathead: Source;
  evanmiyaTeam: Source;
  realGMTeam: Source;
  statheadTeam: Source;
}

interface Note {
  id?: number;
  content: string;
  createdDate?: string;
  updatedDate?: string;
  playerId: number;
  userId?: number;
  userName?: string;
  // Computed properties for HTML template
  date?: Date;
  // UI state
  isEditing?: boolean;
  editContent?: string;
}

@Component({
  selector: 'app-enhanced-player-popover',
  styleUrls: ['./enhanced-player-popover.component.scss'],
  templateUrl: './enhanced-player-popover.component.html',
})
export class EnhancedPlayerPopoverComponent implements OnInit, OnDestroy {
  private _playerId: number;

  @Input() set playerId(value: number | undefined) {
    if (value) {
      this._playerId = value;
      // Reset dataLoaded when playerId changes to force reload
      if (this._playerId && this.dataLoaded) {
        this.dataLoaded = false;
      }
    }
  }
  get playerId() {
    return this._playerId;
  }
  @Input() playerName = '';

  @Output() success = new EventEmitter<boolean>();

  // Dialog visibility
  dialogVisible = false;
  watchlistDialogVisible = false;
  
  // Loading states
  loading = false;
  dataLoaded = false;

  // Form data
  customPositions = Object.entries(CustomPositions).map(([name, key]) => ({ name, key }));
  watchlistIds: number[] = [];
  selectedWatchlist: number | null = null;
  selectedWatchlists: number[] = []; // For multiselect
  watchlists: WatchlistModel[] = [];
  availableWatchlists: WatchlistModel[] = [];
  subscriptions: Subscription[] = [];
  position: string = '';
  teamId = 0;
  team = '';

  // Watchlist management
  newWatchlistDescription = '';

  // Notes management
  myNotes: Note[] = [];
  allNotes: Note[] = [];
  displayedNotes: Note[] = [];
  activeNotesTab: 'my' | 'all' = 'my';
  newNoteText = '';

  // Sources for lazy loading
  sources: Sources = {
    evanmiya: {
      search: '',
      lastSearchResults: null,
      selectedOption: null,
      filteredOptions: [],
      sourceName: PlayerSources.EvanMiya,
      dataSource: 'apiRateEvanmiyaGet$Json'
    },
    realGM: {
      search: '',
      lastSearchResults: null,
      selectedOption: null,
      filteredOptions: [],
      sourceName: PlayerSources.RealGm,
      dataSource: 'apiRateRealgmGet$Json'
    },
    stathead: {
      search: '',
      lastSearchResults: null,
      selectedOption: null,
      filteredOptions: [],
      sourceName: PlayerSources.Stathead,
      dataSource: 'apiRateStatheadGet$Json'
    },
    evanmiyaTeam: {
      search: '',
      lastSearchResults: null,
      selectedOption: null,
      options: [],
      filteredOptions: [],
      sourceName: PlayerSources.EvanMiya,
    },
    realGMTeam: {
      search: '',
      lastSearchResults: null,
      selectedOption: null,
      options: [],
      filteredOptions: [],
      sourceName: PlayerSources.RealGm,
    },
    statheadTeam: {
      search: '',
      lastSearchResults: null,
      selectedOption: null,
      options: [],
      filteredOptions: [],
      sourceName: PlayerSources.Stathead,
    },
  };

  // Add this property to control the inline add watchlist UI
  showAddWatchlistInput: boolean = false;

  @ViewChild('watchlistInput') watchlistInput: ElementRef;

  constructor(
    private watchlistService: WatchlistService,
    private playerPopoverService: PlayerPopoverService,
    private playerService: PlayerService,
    private rateService: RateService,
    private playerNotesService: PlayerNotesService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private authService: AuthService
  ) { }

  ngOnInit(): void {
    this.subscriptions = [
      this.playerPopoverService.wishlists$.subscribe({
        next: (value) => {
          this.watchlists = [...value];
          this.updateAvailableWatchlists();
          this.resetSelectedWatchlists(this.watchlists);
        }
      }),
      this.playerPopoverService.evanmiyaTeams$.subscribe({
        next: (value) => {
          this.sources.evanmiyaTeam.options = value.map(v => ({ id: v, name: v }));
          this.sources.evanmiyaTeam.filteredOptions = [...this.sources.evanmiyaTeam.options];
        }
      }),
      this.playerPopoverService.realGMTeams$.subscribe({
        next: (value) => {
          this.sources.realGMTeam.options = value.map(v => ({ id: v, name: v }));
          this.sources.realGMTeam.filteredOptions = [...this.sources.realGMTeam.options];
          this.initializeAllTeamDropdowns();
        }
      }),
      this.playerPopoverService.statheadTeams$.subscribe({
        next: (value) => {
          this.sources.statheadTeam.options = value.map(v => ({ id: v, name: v }));
          this.sources.statheadTeam.filteredOptions = [...this.sources.statheadTeam.options];
        }
      }),
    ];
  }

  private resetSelectedWatchlists(value: WatchlistModel[]) {
    this.watchlistIds = value
      .filter(({ details }) => (details?.filter(({ playerId }) => this.playerId === playerId)?.length || 0) > 0)
      .map(({ id }) => id as number);
    
    this.selectedWatchlists = [...this.watchlistIds];
    
    if (!this.selectedWatchlist && this.watchlistIds.length > 0) {
      this.selectedWatchlist = this.watchlistIds[0];
    }
    
    this.updateAvailableWatchlists();
  }

  private updateAvailableWatchlists() {
    this.availableWatchlists = this.watchlists.filter(w => !this.watchlistIds.includes(w.id as number));
  }

  onToggle(event: any) {
    if (!this.dataLoaded) {
      this.loadPlayerData();
    } else {
      this.refreshCurrentValues();
    }
    this.initializeAllTeamDropdowns();
    this.initializeAllPlayerDropdowns();
    this.dialogVisible = true;
  }

  private resetAddWatchlistInputState() {
    this.showAddWatchlistInput = false;
    this.newWatchlistDescription = '';
  }

  onMultiSelectFocus() {
    this.resetAddWatchlistInputState();
  }

  private refreshCurrentValues() {
    const playerWatchlist = this.watchlists.find(w => 
      w.details?.some(detail => detail.playerId === this._playerId)
    );
    if (playerWatchlist && !this.selectedWatchlist) {
      this.selectedWatchlist = playerWatchlist.id as number;
    }
    
    this.resetSelectedWatchlists(this.watchlists);
    
    this.getTeamSources().forEach(sourceKey => {
      const source = this.sources[sourceKey];
      const options = source.options;
      if (options && options.length > 0) {
        source.filteredOptions = [...options];
      }
    });
    
    this.initializeAllPlayerDropdowns();
    
    this.loadNotes(false);
  }

  onDialogHide() {
    this.dialogVisible = false;
    this.showAddWatchlistInput = false;
  }

  private loadPlayerData() {
    this.loading = true;
    this.playerService.apiPlayerOverridesIdGet$Json({ id: this._playerId }).subscribe({
      next: ({ data }) => {
        this.teamId = data?.profile?.teamId || 0;
        this.team = data?.profile?.teamMarket || '';
        this.position = data?.overrides?.position || '';
        
        const evanmiyaPlayerName = data?.overrides?.evanmiyaPlayerName || '';
        const realGMPlayerName = data?.overrides?.realGMPlayerName || '';
        const statheadPlayerName = data?.overrides?.statheadPlayerName || '';
        
        this.sources.evanmiya.selectedOption = evanmiyaPlayerName;
        this.sources.realGM.selectedOption = realGMPlayerName;
        this.sources.stathead.selectedOption = statheadPlayerName;
        
        if (evanmiyaPlayerName) {
          this.sources.evanmiya.filteredOptions = [{ name: evanmiyaPlayerName, id: evanmiyaPlayerName }];
        }
        if (realGMPlayerName) {
          this.sources.realGM.filteredOptions = [{ name: realGMPlayerName, id: realGMPlayerName }];
        }
        if (statheadPlayerName) {
          this.sources.stathead.filteredOptions = [{ name: statheadPlayerName, id: statheadPlayerName }];
        }

        const evanmiyaTeamName = data?.teamOverrides?.find(({ source }) => source?.name === PlayerSources.EvanMiya)?.overriddenTeamName || '';
        const realGMTeamName = data?.teamOverrides?.find(({ source }) => source?.name === PlayerSources.RealGm)?.overriddenTeamName || '';
        const statheadTeamName = data?.teamOverrides?.find(({ source }) => source?.name === PlayerSources.Stathead)?.overriddenTeamName || '';
        
        this.sources.evanmiyaTeam.selectedOption = evanmiyaTeamName;
        this.sources.realGMTeam.selectedOption = realGMTeamName;
        this.sources.statheadTeam.selectedOption = statheadTeamName;
        
        this.ensureTeamOptionInFiltered('evanmiyaTeam', evanmiyaTeamName);
        this.ensureTeamOptionInFiltered('realGMTeam', realGMTeamName);
        this.ensureTeamOptionInFiltered('statheadTeam', statheadTeamName);
        
        setTimeout(() => {
          this.initializeAllTeamDropdowns();
          this.initializeAllPlayerDropdowns();
        }, 100);

        // Set selected watchlist - find the first watchlist this player belongs to
        const playerWatchlist = this.watchlists.find(w => 
          w.details?.some(detail => detail.playerId === this._playerId)
        );
        if (playerWatchlist) {
          this.selectedWatchlist = playerWatchlist.id as number;
        }

        this.resetSelectedWatchlists(this.watchlists);
        this.loadNotes();
        this.dataLoaded = true;
        this.loading = false;
      },
      error: () => {
        this.loading = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Unable to load player data'
        });
      }
    });
  }

  getPlayerSources(): (keyof Sources)[] {
    return ['evanmiya', 'realGM', 'stathead'];
  }

  getTeamSources(): (keyof Sources)[] {
    return ['evanmiyaTeam', 'realGMTeam', 'statheadTeam'];
  }

  getSourceLabel(sourceKey: keyof Sources): string {
    const labels = {
      evanmiya: 'EM',
      realGM: 'WS',
      stathead: 'BMP',
      evanmiyaTeam: 'EM',
      realGMTeam: 'WS',
      statheadTeam: 'BMP'
    };
    return labels[sourceKey] || sourceKey;
  }

  onPlayerFilter(event: any, sourceKey: keyof Sources) {
    const query = event.filter;
    this.onFilter({ query }, sourceKey);
  }

  onTeamFilter(event: any, sourceKey: keyof Sources) {
    const query = event.filter;
    if (!this.sources[sourceKey].dataSource && this.sources[sourceKey].options) {
      if (query === '') {
        // Show all options when filter is empty
        this.sources[sourceKey].filteredOptions = [...this.sources[sourceKey].options!];
      } else {
        // Filter options based on query
        this.sources[sourceKey].filteredOptions = 
          this.sources[sourceKey].options!
            .filter(({ name }) => name.toLowerCase().includes(query.toLowerCase()));
      }
    }
  }

  onFilter({ query }: { query: string; }, source: keyof Sources): void {
    this.sources[source].search = query;
    const timer = this.sources[source].timer;
    if (timer) {
      clearTimeout(timer);
    }
    const dataSource = this.sources[source].dataSource;
    
    if (query.length === 0 && dataSource) {
      this.sources[source].lastSearchResults = null;
    }
    
    if (dataSource) {
      this.sources[source].timer = window.setTimeout(() => {
        chainLoad(() => this.loadPlayers(source), () => {
          // No additional side effects needed here
        }).subscribe();
      }, query.length === 0 ? 0 : 500);
    }
  }

  onFocus(source: keyof Sources): void {
    if (this.sources[source].lastSearchResults?.query === this.sources[source].search) {
      this.sources[source].filteredOptions = [...this.sources[source].lastSearchResults?.results || []];
    } else {
      this.sources[source].filteredOptions = [];
      this.sources[source].lastSearchResults = null;
      if (this.sources[source].dataSource) {
        if (this.sources[source].search === '' && this.sources[source].filteredOptions.length === 0) {
          this.sources[source].search = '';
        }
        chainLoad(() => this.loadPlayers(source), () => {
        }).subscribe();
      } else if (this.sources[source].options) {
        this.sources[source].filteredOptions = [...this.sources[source].options!];
      }
    }
  }

  onClear(source: keyof Sources): void {
    this.sources[source].search = '';
    this.sources[source].lastSearchResults = null;
    this.sources[source].selectedOption = null;
    
    if (this.sources[source].options) {
      this.sources[source].filteredOptions = [...this.sources[source].options!];
    } else {
      this.sources[source].filteredOptions = [];
    }
  }

  onClearPosition(): void {
    this.position = '';
  }

  onClearWatchlist(): void {
    this.selectedWatchlist = null;
  }

  openWatchlistDialog(): void {
    this.playerPopoverService.reset();
    
    this.playerPopoverService.wishlists$.pipe(
      take(1)
    ).subscribe((watchlists: WatchlistModel[]) => {
      this.watchlists = [...watchlists];
      this.resetSelectedWatchlists(this.watchlists);
      this.updateAvailableWatchlists();
    });
    
    this.watchlistDialogVisible = true;
  }

  getWatchlistPlaceholder(): string {
    if (this.watchlistIds.length === 0) {
      return 'Select Watchlists';
    } else if (this.watchlistIds.length === 1) {
      const watchlist = this.watchlists.find(w => w.id === this.watchlistIds[0]);
      return watchlist?.description || 'Select Watchlists';
    } else {
      return `${this.watchlistIds.length} watchlists selected`;
    }
  }

  private loadPlayers(source: keyof Sources): Observable<void> {
    const sourceFn =
      this.rateService[this.sources[source].dataSource!] as
        (params: any) => Observable<StringListResult>;
    
    const searchTerm = this.sources[source].search || '';
    
    return sourceFn.bind(this.rateService)({
      PlayerName: searchTerm,
      PageSize: searchTerm === '' ? 20 : 10
    }).pipe(
      map((response) => {
        if (!response?.data) {
          this.clearPlayerData(source);
          return;
        }
        this.sources[source].filteredOptions = response.data
          .filter(p => p)
          .map((p) => ({
            name: p,
            id: p
          }));

        this.sources[source].lastSearchResults = {
          query: this.sources[source].search,
          results: [...this.sources[source].filteredOptions]
        };
      }),
      catchError(() => {
        this.clearPlayerData(source);
        return of(void 0);
      })
    );
  }

  private clearPlayerData(source: keyof Sources): void {
    this.sources[source].filteredOptions = [];
    this.sources[source].lastSearchResults = null;
  }

  private initializeAllTeamDropdowns(): void {
    this.getTeamSources().forEach(sourceKey => {
      const source = this.sources[sourceKey];
      const options = source.options;
      if (options && options.length > 0) {
        source.filteredOptions = [...options];
      }
    });
  }

  private initializeAllPlayerDropdowns(): void {
    this.getPlayerSources().forEach(sourceKey => {
      const source = this.sources[sourceKey];
      if (!source.lastSearchResults || source.filteredOptions.length === 0) {
        source.search = '';
        chainLoad(() => this.loadPlayers(sourceKey), () => {
        }).subscribe();
      }
    });
  }

  private ensureTeamOptionInFiltered(sourceKey: keyof Sources, teamName: string): void {
    const source = this.sources[sourceKey];
    
    if (source.options) {
      if (teamName) {
        const existsInOptions = source.options.some(option => option.name === teamName);
        if (!existsInOptions) {
          source.options.push({ name: teamName, id: teamName });
        }
      }
      source.filteredOptions = [...source.options];
    }
  }

  getWatchlistDescription(watchlistId: number): string {
    return this.watchlists.find(w => w.id === watchlistId)?.description || '';
  }

  removeFromWatchlist(watchlistId: number) {
    this.watchlistIds = this.watchlistIds.filter(id => id !== watchlistId);
    this.updateAvailableWatchlists();
    this.messageService.add({
      severity: 'success',
      summary: 'Success',
      detail: 'Player removed from watchlist'
    });
  }

  addToWatchlist() {
    if (this.selectedWatchlist && !this.watchlistIds.includes(this.selectedWatchlist)) {
      this.watchlistIds.push(this.selectedWatchlist);
      this.selectedWatchlist = null;
      this.messageService.add({
        severity: 'success',
        summary: 'Success',
        detail: 'Player added to watchlist'
      });
    }
  }

  createWatchlistInline() {
    if (!this.newWatchlistDescription.trim()) return;
    this.loading = true;
    this.watchlistService.apiWatchlistCreatePost$Json$Json({
      body: {
        description: this.newWatchlistDescription
      }
    }).subscribe({
      next: ({ data: watchlist }) => {
        if (watchlist && watchlist.id) {
          this.newWatchlistDescription = '';
          this.showAddWatchlistInput = false;
          this.playerPopoverService.reset();
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Watchlist created successfully'
          });
        }
        this.loading = false;
      },
      error: () => {
        this.loading = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Unable to create watchlist'
        });
      }
    });
  }

  cancelInlineWatchlist() {
    this.showAddWatchlistInput = false;
    this.newWatchlistDescription = '';
  }

  private loadNotes(setLoading: boolean = true) {
    if (!this.playerId) return;
    
    if (setLoading) {
      this.loading = true;
    }
    
            this.playerNotesService.apiPlayerNotesPlayerPlayerIdGet$Json({
      playerId: this.playerId!,
      OnlyMyNotes: false
    }).pipe(
      catchError(error => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Unable to load notes'
        });
        this.loading = false;
        return of({ data: [] });
      })
    ).subscribe(response => {
      this.allNotes = (response.data || []).map(dto => this.mapPlayerNoteDto(dto));
      
              this.playerNotesService.apiPlayerNotesPlayerPlayerIdGet$Json({
        playerId: this.playerId!,
        OnlyMyNotes: true
      }).pipe(
        catchError(error => {
          this.loading = false;
          return of({ data: [] });
        })
      ).subscribe(myNotesResponse => {
        this.myNotes = (myNotesResponse.data || []).map(dto => this.mapPlayerNoteDto(dto));
        this.updateDisplayedNotes();
        this.loading = false;
      });
    });
  }

  private mapPlayerNoteDto(dto: PlayerNoteDto): Note {
    const content = dto.content || '';
    const mappedNote = {
      id: dto.id,
      content: content,
      createdDate: dto.createdDate,
      updatedDate: dto.updatedDate,
      playerId: dto.playerId || 0,
      userId: dto.userId,
      userName: dto.userName || undefined,
      // Computed properties for template
      date: dto.createdDate ? new Date(dto.createdDate) : new Date()
    };
    return mappedNote;
  }

  setActiveNotesTab(tab: 'my' | 'all') {
    this.activeNotesTab = tab;
    this.updateDisplayedNotes();
  }

  private updateDisplayedNotes() {
    this.displayedNotes = this.activeNotesTab === 'my' ? this.myNotes : this.allNotes;
  }

  clearNote() {
    this.newNoteText = '';
  }

  addNote() {
    if (!this.newNoteText.trim() || !this.playerId) return;

    this.loading = true;

    this.playerNotesService.apiPlayerNotesPost$Json$Json({
      body: {
        content: this.newNoteText,
        playerId: this.playerId
      } as CreatePlayerNoteCommand
    }).pipe(
      catchError(error => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Unable to create note'
        });
        this.loading = false;
        return of(null);
      })
    ).subscribe(response => {
      if (response?.data) {
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: 'Note created successfully'
        });
        this.clearNote();
        this.loadNotes(false);
      } else {
        this.loading = false;
      }
    });
  }

  startEditNote(note: Note) {
    // Cancel any other note being edited
    this.displayedNotes.forEach(n => {
      if (n.isEditing) {
        n.isEditing = false;
        n.editContent = '';
      }
    });
    
    // Start editing this note
    note.isEditing = true;
    note.editContent = note.content;
  }

  cancelEditNote(note: Note) {
    note.isEditing = false;
    note.editContent = '';
  }

  saveEditedNote(note: Note) {
    if (!note.editContent?.trim() || !note.id) return;

    this.loading = true;

    this.playerNotesService.apiPlayerNotesPut$Json$Json({
      body: {
        id: note.id,
        content: note.editContent
      } as UpdatePlayerNoteCommand
    }).pipe(
      catchError(error => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Unable to update note'
        });
        this.loading = false;
        return of(null);
      })
    ).subscribe(response => {
      if (response?.data) {
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: 'Note updated successfully'
        });
        note.isEditing = false;
        note.editContent = '';
        this.loadNotes(false);
      } else {
        this.loading = false;
      }
    });
  }

  deleteNote(note: Note) {
    if (!note.id) return;

    this.loading = true;

    this.playerNotesService.apiPlayerNotesIdDelete$Json({
      id: note.id
    }).pipe(
      catchError(error => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Unable to delete note'
        });
        this.loading = false;
        return of(null);
      })
    ).subscribe(response => {
      this.messageService.add({
        severity: 'success',
        summary: 'Success',
        detail: 'Note deleted successfully'
      });
              this.loadNotes(false);
    });
  }

  confirmDeleteNote(note: Note) {
    this.confirmationService.confirm({
      message: 'Are you sure you want to delete this note?',
      header: 'Delete Note',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Delete',
      rejectLabel: 'Cancel',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => {
        this.deleteNote(note);
      }
    });
  }

  // Dialog actions
  onSave() {
    this.loading = true;
    // Sync watchlistIds with selectedWatchlists before saving
    this.watchlistIds = [...this.selectedWatchlists];
    
    this.playerService.apiPlayerOptionsPost$Json$Json({
      body: {
        watchlistIds: this.selectedWatchlists,
        position: this.position || null, // Send null explicitly when position is cleared
        playerId: this.playerId || 0,
        evanmiyaSource: this.sources.evanmiya.selectedOption || '',
        realGMSource: this.sources.realGM.selectedOption || '',
        statheadSource: this.sources.stathead.selectedOption || '',
        teamOverrides: Object.values(this.sources)
          .filter(({ dataSource }: Source) => !dataSource)
          .map(({ selectedOption, sourceName }) =>
            ({ source: sourceName, cbbTeamId: this.teamId, overriddenTeamName: selectedOption || '' } as TeamOverridesDto)
          )
      }
    }).subscribe({
      next: () => {
        this.success.emit(true);
        this.loading = false;
        this.playerPopoverService.reset();
        this.dialogVisible = false;
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: 'Data saved successfully'
        });
      },
      error: () => {
        this.success.emit(false);
        this.loading = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Unable to save data'
        });
      }
    });
  }

  onCancel() {
    this.dialogVisible = false;
  }

  // Multiselect footer actions
  openCreateWatchlistDialog() {
    this.openWatchlistDialog();
  }

  clearAllWatchlists() {
    this.selectedWatchlists = [];
    this.messageService.add({
      severity: 'info',
      summary: 'Cleared',
      detail: 'All watchlists have been removed from selection'
    });
  }

  cancelCreateWatchlist() {
    this.watchlistDialogVisible = false;
    this.newWatchlistDescription = '';
  }

  onItemClick(watchlist: WatchlistModel, event: Event) {
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(s => s.unsubscribe());
  }

  onDeleteWatchlistClick(watchlist: WatchlistModel, event: Event) {
    event.stopPropagation();
    this.confirmDeleteWatchlist(watchlist.id!);
  }

  onFooterClick(event: PointerEvent, show: boolean) {
    event.preventDefault();
    event.stopPropagation();
    this.showAddWatchlistInput = show;
    if (!show) {
      this.newWatchlistDescription = '';
    } else {
      setTimeout(() => {
        this.watchlistInput?.nativeElement?.focus();
      }, 0);
    }
  }

  onAddWatchlist() {
    this.createWatchlistInline();
  }

  confirmDeleteWatchlist(watchlistId: number) {
    const watchlist = this.watchlists.find(w => w.id === watchlistId);
    if (!watchlist) return;

    this.confirmationService.confirm({
      message: `Are you sure you want to delete "${watchlist.description}"?`,
      header: 'Delete Watchlist',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Delete',
      rejectLabel: 'Cancel',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => {
        this.deleteWatchlist(watchlistId);
      }
    });
  }

  private deleteWatchlist(watchlistId: number) {
    this.loading = true;
    
    this.watchlistService.apiWatchlistIdDelete$Json({
      id: watchlistId
    }).pipe(
      catchError(error => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Unable to delete watchlist'
        });
        this.loading = false;
        return of(null);
      })
    ).subscribe(response => {
      this.watchlistIds = this.watchlistIds.filter(id => id !== watchlistId);
      this.selectedWatchlists = this.selectedWatchlists.filter(id => id !== watchlistId);
      this.watchlists = this.watchlists.filter(w => w.id !== watchlistId);
      this.updateAvailableWatchlists();
      
      this.playerPopoverService.reset();
      
      this.loading = false;
      this.messageService.add({
        severity: 'success',
        summary: 'Success',
        detail: 'Watchlist deleted successfully'
      });
    });
  }

  canDeleteWatchlist(watchlist: WatchlistModel): boolean {
    if (!this.authService.userData || !watchlist.userId) {
      return false;
    }
    return this.authService.userData.id === watchlist.userId;
  }

  canDeleteNote(note: Note): boolean {
    if (!this.authService.userData || !note.userId) {
      return false;
    }
    return this.authService.userData.id === note.userId;
  }
}